// Custom GA4 event tracking. Events go directly to GA4 via the gtag.js instance
// loaded by the Google & YouTube sales channel pixel (not via GTM).
// To add a new tracker: write an init function or delegated listener below and
// send events with track(name, params).
//
// 2026-10-01: shipped with the merged live theme (Crediblesale carousel + in-theme
// PDP revamp). Adds the PDP revamp events specified in
// audit/2026-09-25_pdp-revamp-tracking-check.md, a Clarity arm tag, sets the
// experiment_arm user property on every page (was homepage only) and retires the
// sticky_variant_edit_clicked listener whose pencil button no longer exists.
//
// 2026-10-06: adds cart_icon_clicked and search_icon_clicked for the header
// icons, which no event covered.
(function () {
  var GA_TAG_ID = 'G-JWBM19CBB5'; // GA4 tag configured in the Google & YouTube channel

  // gtag.js processes Arguments objects pushed to window.dataLayer. Must push
  // `arguments` itself — a plain array or object is ignored by gtag.js.
  function gtag() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
  }

  // --- Experiment arm stamp (2026-09-18) ------------------------------------
  // Shopify Rollouts splits traffic server-side by theme and puts NOTHING into
  // GA4. Without this stamp every GA4 metric is blended across both arms of the
  // homepage test and the test cannot be read at all. window.Shopify.theme is
  // present on every page. Register experiment_arm in GA4 as a session-scoped
  // custom dimension or it stays invisible.
  //
  // With no test running the value is simply the live theme's name, which makes
  // it the clean before/after cut for a release: sessions stamped with the new
  // theme name saw the new build, full stop.
  var armCache;
  function experimentArm() {
    if (armCache !== undefined) return armCache;
    var t = window.Shopify && window.Shopify.theme;
    armCache = t && t.name ? String(t.name).trim() : null;
    return armCache;
  }

  // First homepage module a visitor engages with, held for the session so a
  // later purchase can be attributed back to it. Written once, so it reads as
  // "first module engaged" rather than "last thing clicked".
  var ENTRY_KEY = 'z21_home_entry_module';
  function entryModule() {
    try { return sessionStorage.getItem(ENTRY_KEY); } catch (e) { return null; }
  }
  function setEntryModule(name) {
    if (!name) return;
    try {
      if (sessionStorage.getItem(ENTRY_KEY)) return;
      sessionStorage.setItem(ENTRY_KEY, name);
      gtag('set', 'user_properties', { home_entry_module: name });
    } catch (e) { /* private mode, blocked storage */ }
  }

  function track(eventName, params) {
    var base = { send_to: GA_TAG_ID };
    var arm = experimentArm();
    if (arm) base.experiment_arm = arm;
    var entry = entryModule();
    if (entry) base.home_entry_module = entry;
    gtag('event', eventName, Object.assign(base, params));
  }

  // Exposed for trackers that need data only their own flow has (e.g.
  // assets/cart-promo-code.js), so there is still one gtag path.
  window.trackCustomEvent = track;

  // --- Cart CTA clicks (drawer + cart page) ---------------------------------
  // Capture phase: immune to stopPropagation in theme handlers and AJAX re-renders.
  var CTA_MAP = {
    'CartDrawer-ViewCart': { cta_type: 'view_cart', cta_location: 'drawer' },
    'CartDrawer-Checkout': { cta_type: 'checkout', cta_location: 'drawer' },
    'checkout': { cta_type: 'checkout', cta_location: 'cart_page' }
  };

  document.addEventListener('click', function (event) {
    // Upsell app embeds (lb-upsell) intercept checkout clicks and re-dispatch a
    // synthetic click; count user-initiated clicks only to avoid double-counting.
    if (!event.isTrusted) return;
    if (!event.target || !event.target.closest) return;
    var cta = event.target.closest('#CartDrawer-ViewCart, #CartDrawer-Checkout, #checkout');
    if (cta && CTA_MAP[cta.id]) track('cart_cta_clicked', CTA_MAP[cta.id]);
  }, true);

  // --- Cart drawer opened ---------------------------------------------------
  // Fire once per closed -> open transition of 'active'. DrawerFixed.close()
  // removes only 'active' (keeps 'animate'), so key on 'active' alone.
  function initDrawerTracking() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return; // cart page, notification cart type, or drawer disabled
    var wasActive = drawer.classList.contains('active');
    new MutationObserver(function () {
      var isActive = drawer.classList.contains('active');
      if (isActive && !wasActive) track('cart_drawer_opened', {});
      wasActive = isActive;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  // --- Header icons: search + cart (2026-10-06) ------------------------------
  // The header sits outside every tracked module, so GA4 had no click for these
  // two icons: only cart_drawer_opened (which also fires after an add to cart)
  // and view_search_results (a finished search, not the tap).
  // Live DOM, probed 2026-10-06 on both breakpoints:
  //   cart    a#cart-icon-bubble.header__icon--cart. Desktop renders a second,
  //           hidden copy under .header__icons, so read the count from the
  //           link that was clicked, never by id.
  //   search  details-modal.header__search > details > summary.header__icon--search
  //           opens the #Search-In-Modal overlay; mobile also has a search field
  //           at the top of the burger drawer, form.ab-burger-header-search.
  // cta_location is where the icon sat: header, or drawer when the burger drawer
  // was open. cart_count is the badge number at the moment of the click.
  function burgerOpen() {
    var d = document.getElementById('Details-menu-drawer-container');
    return !!(d && d.open);
  }

  function badgeCount(link) {
    // the bubble holds the number twice (aria-hidden + visually-hidden text),
    // so read the aria-hidden span only or "2" becomes "22"
    var n = link && link.querySelector('.cart-count-bubble [aria-hidden="true"]');
    var v = n ? parseInt((n.textContent || '').replace(/[^\d]/g, ''), 10) : 0;
    return isNaN(v) ? 0 : v;
  }

  var drawerSearchSent = false;
  document.addEventListener('click', function (event) {
    if (!event.isTrusted || !event.target || !event.target.closest) return;
    var cart = event.target.closest('a.header__icon--cart, #cart-icon-bubble');
    if (cart) {
      track('cart_icon_clicked', {
        cta_location: burgerOpen() ? 'drawer' : 'header',
        cart_count: badgeCount(cart)
      });
      return;
    }
    var toggle = event.target.closest('.header__search summary');
    if (toggle) {
      var det = toggle.closest('details');
      if (det && det.open) return; // this click closes the overlay
      track('search_icon_clicked', { cta_location: 'header' });
      return;
    }
    // drawer field: count the first tap per page, not every tap into the field
    if (!drawerSearchSent && event.target.closest('.ab-burger-header-search')) {
      drawerSearchSent = true;
      track('search_icon_clicked', { cta_location: 'drawer' });
    }
  }, true);

  // --- Menu tracking: mobile burger drawer + desktop mega menu ---------------
  // Rewritten 2026-09-01. The 27 Aug menu revamp replaced the theme's
  // <details>/<summary> drawer markup with the ab-burger panel model, so the
  // old `summary.menu-drawer__menu-item` hook matched nothing and
  // menu_submenu_opened stopped firing (GA4 mobile: 25.2 events per 1k
  // sessions on 22 Aug, 0.0 from 28 Aug). Confirmed against the live DOM on
  // 2026-09-01: zero elements match summary.menu-drawer__menu-item.
  //
  // Live structure, probed rather than read off the Liquid source:
  //   mobile   #menu-drawer .ab-burger
  //            button.ab-burger-category-row   opens a category panel
  //            button.ab-burger-focus-banner   opens a focus panel
  //            a.ab-burger-product-card, a.ab-burger-other-row
  //            .ab-burger-panel[data-panel-id][data-title], .is-active
  //   desktop  details[data-ab-dmega] > summary.header__menu-item--top
  //            .mm-content with a.mm-card / a.mm-chip / a.mm-cta
  //
  // The drawer open itself still keys on the theme's own burger <details>,
  // which the revamp left in place, so menu_drawer_opened never broke.
  //
  // New params on both events: category, menu_surface, panel_type, link_type.
  // These need registering as event-scoped custom dimensions in GA4 or they
  // are collected but not queryable in reports or the Data API.

  var MEGA_DETAILS = 'details[data-ab-dmega]';
  var DESKTOP_DWELL_MS = 400; // mega panels open on hover: ignore mouse fly-past

  function initMenuDrawerTracking() {
    var details = document.getElementById('Details-menu-drawer-container');
    if (!details) return;
    details.addEventListener('toggle', function () {
      if (details.open) track('menu_drawer_opened', {});
    });
  }

  // GA4 caps param values at 100 chars. Cards can be image-only, so fall back
  // to aria-label then img alt.
  function linkLabel(el) {
    var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!text) text = el.getAttribute('aria-label') || '';
    if (!text) {
      var img = el.querySelector && el.querySelector('img[alt]');
      if (img) text = img.getAttribute('alt') || '';
    }
    return text.slice(0, 100);
  }

  // Cards wrap title, subtitle and price inside one <a>, so textContent would
  // carry the price into the dimension and mint a brand-new value for the same
  // product every time its price changes. Read the title node when there is one.
  var TITLE_SELECTORS = [
    '.mm-card__title', '.mm-chip__zh',
    '.ab-burger-product-card__title', '.ab-burger-other-row__title',
    '.ab-burger-category-row__title', '.ab-burger-focus-banner__title'
  ].join(', ');

  function menuTitle(el) {
    var t = el.querySelector && el.querySelector(TITLE_SELECTORS);
    if (t) {
      var s = (t.textContent || '').replace(/\s+/g, ' ').trim();
      if (s) return s.slice(0, 100);
    }
    return linkLabel(el);
  }

  function textOf(root, selector) {
    var el = root && root.querySelector(selector);
    return el ? (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100) : '';
  }

  // Which surface an element sits in, and which category panel it belongs to.
  // category is '' for the drawer home panel and for top-level nav links.
  function menuContext(el) {
    var mega = el.closest(MEGA_DETAILS);
    if (mega) return { surface: 'megamenu', category: textOf(mega, 'summary') };
    if (el.closest('#menu-drawer')) {
      var panel = el.closest('.ab-burger-panel');
      var id = panel ? (panel.dataset.panelId || '') : '';
      return {
        surface: 'drawer',
        category: (panel && id !== 'home') ? (panel.dataset.title || '').slice(0, 100) : ''
      };
    }
    return null;
  }

  function linkType(el) {
    if (el.closest('.ab-burger-product-card') || el.closest('.mm-card')) return 'product_card';
    if (el.closest('.mm-chip')) return 'chip';
    if (el.closest('.mm-cta')) return 'cta_view_all';
    if (el.closest('.ab-burger-other-row')) return 'other_row';
    return 'plain';
  }

  // Panel opens inside the mobile drawer. Both controls are <button>, not <a>,
  // so they never fall through to the menu_link_clicked branch below.
  document.addEventListener('click', function (event) {
    if (!event.isTrusted) return;
    if (!event.target || !event.target.closest) return;

    var row = event.target.closest('.ab-burger-category-row, .ab-burger-focus-banner');
    if (row) {
      var isFocus = row.classList.contains('ab-burger-focus-banner');
      var name = menuTitle(row);
      track('menu_submenu_opened', {
        category: name,
        menu_surface: 'drawer',
        panel_type: isFocus ? 'focus' : 'category',
        link_text: name, // kept so the pre-27-Aug series stitches on
        menu_level: 2
      });
      return;
    }

    var link = event.target.closest('a[href]');
    if (!link) return;
    var ctx = menuContext(link);
    if (!ctx) return;
    track('menu_link_clicked', {
      category: ctx.category,
      menu_surface: ctx.surface,
      link_type: linkType(link),
      link_text: menuTitle(link),
      link_url: (link.getAttribute('href') || '').slice(0, 100),
      menu_level: ctx.category ? 2 : 1
    });
  }, true);

  // Desktop mega panels. `toggle` does not bubble, and the panels may get
  // data-ab-dmega after DOMContentLoaded, so watch the `open` attribute via
  // MutationObserver instead of binding each <details> at init.
  function armMega(det) {
    if (det.open) {
      det._z21MegaTimer = setTimeout(function () {
        det._z21MegaTimer = null;
        // The theme leaves a panel open after the pointer moves off it, so
        // "still open" is not evidence of intent: one mouse sweep across the
        // nav bar leaves every panel it crossed open, and all of them would
        // clear a plain time-only gate. Require the pointer to still be inside
        // the panel, or keyboard focus to be in it, at the end of the window.
        if (!det.matches(':hover') && !det.contains(document.activeElement)) return;
        var name = textOf(det, 'summary');
        track('menu_submenu_opened', {
          category: name,
          menu_surface: 'megamenu',
          panel_type: 'category',
          link_text: name,
          menu_level: 2
        });
      }, DESKTOP_DWELL_MS);
    } else if (det._z21MegaTimer) {
      clearTimeout(det._z21MegaTimer);
      det._z21MegaTimer = null;
    }
  }

  function initMegaMenuTracking() {
    if (!window.MutationObserver) return;
    var scope = document.querySelector('header') || document.body;
    if (!scope) return;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var t = records[i].target;
        if (t && t.matches && t.matches(MEGA_DETAILS)) armMega(t);
      }
    }).observe(scope, { attributes: true, attributeFilter: ['open'], subtree: true });
  }

  // --- PDP add-to-cart clicks (main button vs sticky bar) --------------------
  // Both main-product sections render the sticky bar as a sibling of the
  // <section id="MainProduct-..."> wrapper, so test sticky-cart first.
  // Quick-add cards, quick view, featured-product, and cart drawer upsell ATCs
  // fall through both checks and are deliberately not counted.
  document.addEventListener('click', function (event) {
    if (!event.isTrusted) return;
    if (!event.target || !event.target.closest) return;
    var btn = event.target.closest('button[name="add"]');
    if (!btn) return;
    if (btn.closest('sticky-cart')) {
      track('atc_clicked', { atc_location: 'sticky_bar' });
    } else if (btn.closest('[id^="MainProduct-"]')) {
      track('atc_clicked', { atc_location: 'pdp_main' });
    }
  }, true);

  // --- Sticky bar variant interactions ---------------------------------------
  // The revamped bar carries the theme's own <variant-selects> dropdown on
  // mobile and desktop. isTrusted filters the synthetic change events from the
  // theme's main-picker <-> sticky sync, so only changes the user makes in the
  // bar itself are counted. picker_location added 2026-10-01 so this event and
  // pdp_variant_changed (main picker) read side by side.
  document.addEventListener('change', function (event) {
    if (!event.isTrusted) return;
    var el = event.target;
    if (!el || el.tagName !== 'SELECT' || !el.closest) return;
    if (!el.closest('sticky-cart')) return;
    // Selects are named options[<Option name>], e.g. options[Color]
    var m = (el.name || '').match(/^options\[(.+)\]$/);
    track('sticky_variant_changed', {
      option_name: (m ? m[1] : 'option').slice(0, 100),
      option_value: (el.value || '').slice(0, 100),
      picker_location: 'sticky_bar'
    });
  }, true);

  // sticky_variant_edit_clicked (the old mobile pencil button) retired
  // 2026-10-01: the revamped bar no longer renders that control.

  // --- Homepage module tracking (2026-09-18) --------------------------------
  // Built for the Strongsale vs Crediblesale homepage test. Rollouts reports only
  // store-wide conversion rate, AOV, revenue and bounce, with no custom goals, so
  // these events are the only way to answer which homepage module earns the click
  // and carries the revenue. From 2026-10-01 the live homepage carries BOTH
  // arms' modules: the hero product bar with SHOP NOW and the credibility
  // carousel (banner_grid_9NHPbt), so both branches below are live at once.
  //
  // Only homepage TEMPLATE sections are counted. Header, footer and the header
  // group's own announcement bar are id-prefixed 'sections--', not 'template--',
  // so they are excluded and cannot pollute the module attribution.
  var HOME_SECTION_RE = /^shopify-section-template--/;

  function sectionKey(id) {
    return id.replace(/^shopify-section-(template--\d+__)?/, '');
  }

  function homeModuleName(id) {
    if (/banner_grid_9NHPbt/.test(id)) return 'campaign_banner';
    if (/banner_grid_RYF4Nw/.test(id)) return 'category_tile';
    if (/announcement_bar/.test(id)) return 'trust_bar';
    if (/text_icons/.test(id)) return 'text_icons';
    if (/convx_awards_detail/.test(id)) return 'awards_detail';
    if (/518b8ced/.test(id)) return 'hero_slideshow';
    return null; // not a module we attribute revenue to
  }

  var ITEM_SELECTOR = '.banner-grid__item, [id^="Slide-"], [id^="PBar-"], .text-icons__item';

  function itemInfo(target, sec) {
    var item = target.closest ? target.closest(ITEM_SELECTOR) : null;
    if (!item) return { position: null, label: null };
    var siblings = sec.querySelectorAll(ITEM_SELECTOR);
    var pos = Array.prototype.indexOf.call(siblings, item);
    var label = (item.textContent || '').replace(/\s+/g, ' ').trim();
    if (!label) {
      var img = item.querySelector('img');
      var src = img && (img.currentSrc || img.getAttribute('src') || '');
      label = (img && img.getAttribute('alt')) || (src ? src.split('/').pop().split('?')[0] : '');
    }
    return { position: pos >= 0 ? pos + 1 : null, label: label ? label.slice(0, 80) : null };
  }

  function targetOf(anchor) {
    var href = anchor && anchor.getAttribute ? (anchor.getAttribute('href') || '') : '';
    function seg(marker) { return href.split(marker)[1].split(/[?#]/)[0]; }
    if (href.indexOf('/products/') > -1) return { target_type: 'product', target_id: seg('/products/') };
    if (href.indexOf('/collections/') > -1) return { target_type: 'collection', target_id: seg('/collections/') };
    if (href.indexOf('/pages/') > -1) return { target_type: 'page', target_id: seg('/pages/') };
    return { target_type: href ? 'other' : 'none', target_id: null };
  }

  function initHomepageTracking() {
    if (!document.body || !document.body.classList.contains('index')) return;

    // --- clicks ---
    document.addEventListener('click', function (event) {
      if (!event.isTrusted || !event.target || !event.target.closest) return;
      var sec = event.target.closest('[id^="shopify-section-"]');
      if (!sec || !HOME_SECTION_RE.test(sec.id)) return;
      var module = homeModuleName(sec.id);
      if (!module) return;
      if (event.target.closest('[id^="PBar-"]')) module = 'hero_product_bar';

      var anchor = event.target.closest('a[href]');
      var control = event.target.closest('a[href], button');
      // carousel arrows are navigation, not module engagement
      if (control && control.tagName === 'BUTTON' && control.closest('.slider-buttons, .slider-button')) return;

      var info = itemInfo(event.target, sec);
      var tgt = targetOf(anchor);

      setEntryModule(module);
      track('home_module_click', Object.assign({
        module: module,
        module_id: sectionKey(sec.id),
        position: info.position,
        label: info.label
      }, tgt));

      if (module === 'hero_product_bar') {
        var text = control ? (control.textContent || '').replace(/\s+/g, ' ').trim() : '';
        track('home_product_bar_click', {
          cta: /shop\s*now/i.test(text) ? 'shop_now' : 'product_link',
          product_id: tgt.target_id,
          position: info.position
        });
      }

      if (tgt.target_type === 'product') {
        track('home_to_pdp', { source_module: module, product_id: tgt.target_id });
      }
    }, true);

    // --- module impressions: 50% visible, held for 1s, once per page ---
    if (typeof IntersectionObserver === 'function') {
      var timers = {};
      var fired = {};
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var sec = entry.target;
          var id = sec.id;
          if (fired[id]) return;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            if (timers[id]) return;
            timers[id] = setTimeout(function () {
              fired[id] = true;
              timers[id] = null;
              track('home_module_view', { module: homeModuleName(id), module_id: sectionKey(id) });
              io.unobserve(sec);
            }, 1000);
          } else if (timers[id]) {
            clearTimeout(timers[id]);
            timers[id] = null;
          }
        });
      }, { threshold: [0, 0.5, 1] });

      Array.prototype.forEach.call(document.querySelectorAll('[id^="shopify-section-"]'), function (sec) {
        if (HOME_SECTION_RE.test(sec.id) && homeModuleName(sec.id)) io.observe(sec);
      });
    }

    // --- hero slide views ---
    // SliderComponent.update() already dispatches slideChanged, so hook it
    // rather than polling. Reports each slide once per page.
    var heroTrack = document.querySelector('[id^="Slider-"][data-has-video-slide]');
    var hero = heroTrack && heroTrack.closest('slideshow-component, slider-component');
    if (hero) {
      var slideSeen = {};
      var reportSlide = function (slide, index) {
        if (!slide || slideSeen[slide.id]) return;
        slideSeen[slide.id] = true;
        var heading = slide.querySelector('.banner__heading, h2, h3');
        var title = heading ? (heading.textContent || '').replace(/\s+/g, ' ').trim() : '';
        if (!title) {
          // These slides are pure artwork with no heading element, so fall back
          // to the alt text and then the filename, as home_module_click does.
          var img = slide.querySelector('img');
          var src = img && (img.currentSrc || img.getAttribute('src') || '');
          title = (img && img.getAttribute('alt')) || (src ? src.split('/').pop().split('?')[0] : '');
        }
        track('home_hero_slide_view', {
          slide_index: index || null,
          slide_title: title ? title.slice(0, 80) : null,
          is_video: slide.classList.contains('slideshow__slide--video')
        });
      };
      hero.addEventListener('slideChanged', function (event) {
        var d = event.detail || {};
        reportSlide(d.currentElement, d.currentPage);
      });
      reportSlide(heroTrack.querySelector('[id^="Slide-"]'), 1);
    }

    // --- scroll depth ---
    // The two arms are very different page lengths, so how far people get is a
    // first-class comparison, not a vanity metric.
    var marks = [25, 50, 75, 90];
    var hitMark = {};
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var max = document.documentElement.scrollHeight - window.innerHeight;
        if (max <= 0) return;
        var pct = (window.pageYOffset || document.documentElement.scrollTop) / max * 100;
        for (var i = 0; i < marks.length; i++) {
          if (pct >= marks[i] && !hitMark[marks[i]]) {
            hitMark[marks[i]] = true;
            track('home_scroll_depth', { percent: marks[i] });
          }
        }
      });
    }, { passive: true });
  }

  // --- Session landing stamp (2026-09-18) -----------------------------------
  // One arm-stamped event per session, on whatever page the visitor enters on.
  // Without it a session that lands on a PDP and never touches the cart or menu
  // emits no custom event at all, so it carries no experiment_arm and cannot be
  // assigned to either arm of the homepage test.
  //
  // landing_type = 'homepage' is the denominator for the homepage read: only
  // 6.5% of this store's sessions land on '/', so the comparison has to be built
  // on that population, not on store-wide traffic.
  //
  // Caveat: sessionStorage lasts until the tab closes, while a GA4 session
  // expires after 30 minutes idle, so on long visits this can under-count
  // relative to GA4's own session count. Use it for the arm stamp and for SRM,
  // and take exact session counts from GA4's built-in Landing page dimension.
  var SESSION_KEY = 'z21_session_landed';

  function initSessionLanding() {
    var first;
    try {
      first = !sessionStorage.getItem(SESSION_KEY);
      if (first) sessionStorage.setItem(SESSION_KEY, '1');
    } catch (e) {
      return; // private mode or blocked storage: better to send nothing than to send it on every page
    }
    if (!first) return;

    var path = (window.location && window.location.pathname) || '';
    var isHome = path === '/' || path === '' ||
      (document.body && document.body.classList.contains('index'));

    track('session_landing', {
      landing_type: isHome ? 'homepage' : 'other',
      landing_path: path.slice(0, 100)
    });
  }

  // --- PDP view (2026-09-29) -------------------------------------------------
  // Shopify's view_item carries no experiment arm (0% of 76,407 events were
  // attributable over 22-28 Sep), and home_to_pdp only catches a direct
  // homepage-to-product click, so a visitor who goes homepage -> collection ->
  // product is invisible. This mirrors the product view through track(), which
  // stamps experiment_arm and home_entry_module onto it, closing that chain.
  function initPdpView() {
    if (!document.body || !document.body.classList.contains('product')) return;
    var path = (window.location && window.location.pathname) || '';
    var handle = null;
    if (path.indexOf('/products/') > -1) {
      handle = path.split('/products/')[1].split('?')[0].split('#')[0].split('/')[0];
    }
    track('pdp_view', { product_handle: handle ? handle.slice(0, 100) : null });
  }

  // --- PDP revamp tracking (2026-10-01) --------------------------------------
  // Spec: audit/2026-09-25_pdp-revamp-tracking-check.md, Section 3. Shipped with
  // the in-theme PDP revamp (sections/main-product.liquid, assets/convx-pdp.js,
  // assets/sticky-cart.js). Everything rides through track(), so experiment_arm
  // and home_entry_module are stamped on each event. Param VALUES are queryable
  // only once registered as event-scoped custom dimensions; event COUNTS appear
  // regardless. Registration list: html/2026-10-01_ga4-registrations.md.
  //
  // Hooks, verified against the revamp markup 2026-10-01:
  //   nav      .anchor_point[data-cx-anchor] .anchor_point_inner a  (convx-pdp.js toggles .is-pinned,
  //            normalises hrefs to '#<section element id>', or '#' for 跳返上頂)
  //   bar      sticky-cart                                          (sticky-cart.js toggles .open)
  //   blocks   .productSale_price, product-info button[name=add], .ab-assure, [data-cx-buynote],
  //            [data-cx-perf], [data-cx-usagenote], Judge.me widget, product_specification, faq sections
  //   picker   variant-radios input (name "<Option>-<section id>"), variant-selects select (options[<Option>])
  //   gallery  [id^="Slider-Thumbnails-"] li[data-media-position], .product__slider-button--prev / --next,
  //            .product__modal-opener
  //   rating   [data-cx-review-link] beside [data-cx-rating-score] and [data-cx-rating-count]

  function isProductPage() {
    return !!(document.body && document.body.classList.contains('product'));
  }

  function scrollPercent() {
    var max = document.documentElement.scrollHeight - window.innerHeight;
    if (max <= 0) return 0;
    var pct = (window.pageYOffset || document.documentElement.scrollTop || 0) / max * 100;
    return Math.max(0, Math.min(100, Math.round(pct)));
  }

  // Bucketed so the registered dimension stays low-cardinality.
  function scrollBucket(pct) {
    if (pct >= 90) return '90-100';
    if (pct >= 75) return '75-89';
    if (pct >= 50) return '50-74';
    if (pct >= 25) return '25-49';
    return '0-24';
  }

  function cleanText(el) {
    return el ? (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 100) : '';
  }

  // Section element ids embed a template id that changes on every theme copy
  // (shopify-section-template--N__faq_68XyRn), so report the key after '__'.
  function navTarget(anchor) {
    var href = anchor.getAttribute('href') || '';
    var hash = href.indexOf('#') !== -1 ? href.slice(href.indexOf('#') + 1) : '';
    if (!hash) return 'top';
    if (hash.indexOf('cx-perf') === 0) return 'perf';
    if (hash === 'cx-reviews') return 'reviews';
    var cut = hash.lastIndexOf('__');
    return (cut !== -1 ? hash.slice(cut + 2) : hash).slice(0, 100);
  }

  function initPdpTracking() {
    if (!isProductPage()) return;

    // 1. Section nav pill clicks. Capture phase: runs before convx-pdp.js's own
    //    handler, whose preventDefault does not affect this listener.
    document.addEventListener('click', function (event) {
      if (!event.isTrusted || !event.target || !event.target.closest) return;
      var a = event.target.closest('.anchor_point_inner a[href]');
      if (!a) return;
      var nav = a.closest('.anchor_point');
      track('pdp_nav_click', {
        nav_label: cleanText(a),
        nav_target: navTarget(a),
        nav_pinned: !!(nav && nav.classList.contains('is-pinned')),
        scroll_pct: scrollBucket(scrollPercent())
      });
    }, true);

    // 2. Sticky bar exposure: the first time the bar opens on this page. With
    //    atc_clicked sticky_bar this gives bar add-to-cart per exposed session.
    var bar = document.querySelector('sticky-cart');
    if (bar && window.MutationObserver) {
      var t0 = Date.now();
      var barSeen = false;
      var reportBar = function () {
        if (barSeen || !bar.classList.contains('open')) return;
        barSeen = true;
        track('pdp_sticky_bar_view', {
          trigger: (Date.now() - t0) < 1000 ? 'initial' : 'scroll',
          scroll_y: Math.round(window.pageYOffset || document.documentElement.scrollTop || 0)
        });
      };
      new MutationObserver(reportBar).observe(bar, { attributes: true, attributeFilter: ['class'] });
      reportBar(); // sticky-cart.js may have opened it before this ran
    }

    // 3. PDP scroll depth, same marks as the homepage, scoped to product pages.
    var marks = [25, 50, 75, 90];
    var hit = {};
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var pct = scrollPercent();
        for (var i = 0; i < marks.length; i++) {
          if (pct >= marks[i] && !hit[marks[i]]) {
            hit[marks[i]] = true;
            track('pdp_scroll_depth', { percent: marks[i] });
          }
        }
      });
    }, { passive: true });

    // 4. Block impressions: half the block (or half the viewport, for blocks
    //    taller than the screen) visible for 1s, once per page per block.
    //    Answers "is 使用小提醒 ever seen" and "how far the buy box is read".
    if (typeof IntersectionObserver === 'function') {
      var BLOCKS = [
        ['price', '.productSale_price'],
        ['atc', 'product-info[id^="MainProduct-"] button[name="add"]'],
        ['assure', '.ab-assure'],
        ['buy_note', '[data-cx-buynote]'],
        ['perf_band', '[data-cx-perf]'],
        ['usage_note', '[data-cx-usagenote]'],
        ['reviews', '.jdgm-rev-widg, .jdgm-widget, [class*="jdgm-review-widget"]'],
        ['specs', '.shopify-section[id*="product_specification"]'],
        ['faq', '.shopify-section[id*="__faq"]']
      ];
      var bTimers = {};
      var bFired = {};
      var bObserved = {};
      var bio = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var el = entry.target;
          var name = el.getAttribute('data-z21-block');
          if (!name || bFired[name]) return;
          var need = Math.min(entry.boundingClientRect.height * 0.5, window.innerHeight * 0.5);
          var ok = need > 0 && entry.isIntersecting && entry.intersectionRect.height >= need;
          if (ok) {
            if (bTimers[name]) return;
            bTimers[name] = setTimeout(function () {
              bFired[name] = true;
              bTimers[name] = null;
              var top = Math.round(el.getBoundingClientRect().top + (window.pageYOffset || 0));
              track('pdp_block_view', { block: name, block_top_px: top });
              bio.unobserve(el);
            }, 1000);
          } else if (bTimers[name]) {
            clearTimeout(bTimers[name]);
            bTimers[name] = null;
          }
        });
      }, { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1] });
      var observeBlocks = function () {
        BLOCKS.forEach(function (b) {
          if (bObserved[b[0]]) return;
          var el = document.querySelector(b[1]);
          if (!el) return;
          bObserved[b[0]] = true;
          el.setAttribute('data-z21-block', b[0]);
          bio.observe(el);
        });
      };
      observeBlocks();
      // Judge.me renders its widget after load; pick up late blocks.
      setTimeout(observeBlocks, 3000);
      setTimeout(observeBlocks, 8000);
    }

    // 5. Main picker changes. Radios are named "<Option>-<section id>", selects
    //    "options[<Option>]". The bar's own dropdown is sticky_variant_changed.
    document.addEventListener('change', function (event) {
      if (!event.isTrusted) return;
      var el = event.target;
      if (!el || !el.closest || el.closest('sticky-cart')) return;
      if (!el.closest('variant-radios, variant-selects')) return;
      var name = el.name || '';
      var m = name.match(/^options\[(.+)\]$/);
      if (m) {
        name = m[1];
      } else {
        var pi = el.closest('product-info');
        var sid = pi ? (pi.getAttribute('data-section') || '') : '';
        if (sid && name.slice(-(sid.length + 1)) === '-' + sid) name = name.slice(0, -(sid.length + 1));
      }
      track('pdp_variant_changed', {
        option_name: name.slice(0, 100),
        option_value: (el.value || '').slice(0, 100),
        picker_location: 'pdp_main'
      });
    }, true);

    // 6. Gallery: thumbnail, arrows, zoom.
    document.addEventListener('click', function (event) {
      if (!event.isTrusted || !event.target || !event.target.closest) return;
      var t = event.target;
      var thumb = t.closest('[id^="Slider-Thumbnails-"] li');
      if (thumb) {
        var pos = parseInt(thumb.getAttribute('data-media-position'), 10);
        track('pdp_gallery_click', { gallery_action: 'thumbnail', media_position: isNaN(pos) ? null : pos });
        return;
      }
      if (t.closest('.product__slider-button--prev')) {
        track('pdp_gallery_click', { gallery_action: 'prev', media_position: null });
        return;
      }
      if (t.closest('.product__slider-button--next')) {
        track('pdp_gallery_click', { gallery_action: 'next', media_position: null });
        return;
      }
      var zoom = t.closest('.product__modal-opener');
      if (zoom) {
        var slide = zoom.closest('[id^="Slide-"]');
        var list = slide && slide.parentNode ? slide.parentNode.querySelectorAll('[id^="Slide-"]') : null;
        var zi = list ? Array.prototype.indexOf.call(list, slide) : -1;
        track('pdp_gallery_click', { gallery_action: 'zoom', media_position: zi >= 0 ? zi + 1 : null });
      }
    }, true);

    // 7. The "N 條評論" link beside the stars.
    document.addEventListener('click', function (event) {
      if (!event.isTrusted || !event.target || !event.target.closest) return;
      var link = event.target.closest('[data-cx-review-link]');
      if (!link) return;
      var wrap = link.closest('.rating-wrapper') || document;
      var cnt = parseInt(cleanText(wrap.querySelector('[data-cx-rating-count]')), 10);
      var val = parseFloat(cleanText(wrap.querySelector('[data-cx-rating-score]')));
      track('pdp_review_link_click', {
        review_count: isNaN(cnt) ? null : cnt,
        rating_value: isNaN(val) ? null : val
      });
    }, true);

    // 8. Links inside the two note cards (KOL codes and offer links live there).
    document.addEventListener('click', function (event) {
      if (!event.isTrusted || !event.target || !event.target.closest) return;
      var a = event.target.closest('a[href]');
      if (!a) return;
      var note = a.closest('[data-cx-buynote], [data-cx-usagenote]');
      if (!note) return;
      track('pdp_note_click', {
        note_type: note.hasAttribute('data-cx-buynote') ? 'buy' : 'usage',
        link_url: (a.getAttribute('href') || '').slice(0, 100)
      });
    }, true);
  }

  // --- Clarity arm tag (2026-10-01) -----------------------------------------
  // Clarity loads through its Shopify app embed, so window.clarity may not exist
  // yet when this runs; retry briefly rather than depend on load order. Lets
  // heatmaps and recordings be filtered by theme, which is how the PDP evidence
  // pack (audit/2026-09-25_pdp-revamp-evidence.md) was produced under Varify's tag.
  function initClarityArm() {
    var arm = experimentArm();
    if (!arm) return;
    var tries = 0;
    (function attempt() {
      if (typeof window.clarity === 'function') {
        try { window.clarity('set', 'experiment_arm', arm); } catch (e) { /* ignore */ }
        return;
      }
      if (++tries <= 6) setTimeout(attempt, tries * 1000); // 1s, 2s ... 6s: ~21s in total
    })();
  }

  // The arm as a user property on every page (was homepage only). Registering
  // experiment_arm USER-scoped in GA4 then lets Shopify's own purchase /
  // add_to_cart / view_item events, which carry no arm parameter, be cut by
  // theme as well. User scope is sticky and applies forward only.
  function initArmUserProperty() {
    var arm = experimentArm();
    if (arm) gtag('set', 'user_properties', { experiment_arm: arm });
  }

  function initAll() {
    initArmUserProperty();
    initSessionLanding();
    initPdpView();
    initDrawerTracking();
    initMenuDrawerTracking();
    initMegaMenuTracking();
    initHomepageTracking();
    initPdpTracking();
    initClarityArm();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
